import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { LineChart, Line, ResponsiveContainer, XAxis, YAxis, Tooltip, Bar, BarChart, Cell } from "recharts";
import { Loader2, TrendingUp, Percent, Home, Trees, PoundSterling, X, Download, Printer } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import CustomerPeek, { sourceLabel, paymentLabel } from "@/components/admin/CustomerPeek";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const gbp = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP", maximumFractionDigits: 0 }).format(v || 0);
const today = () => new Date().toISOString().slice(0, 10);
const addDays = (d, n) => { const dt = new Date(d); dt.setDate(dt.getDate() + n); return dt.toISOString().slice(0, 10); };

const PRESETS = [
  { key: "today", label: "Today", range: () => [today(), today()] },
  { key: "week", label: "This week", range: () => [addDays(today(), -6), today()] },
  { key: "month", label: "This month", range: () => { const t = new Date(); return [new Date(t.getFullYear(), t.getMonth(), 1).toISOString().slice(0, 10), today()]; } },
  { key: "year", label: "This year", range: () => { const t = new Date(); return [`${t.getFullYear()}-01-01`, today()]; } },
  { key: "12m", label: "Last 12 months", range: () => [addDays(today(), -365), today()] },
];

const AdminAnalytics = () => {
  const [start, setStart] = useState(PRESETS[3].range()[0]);
  const [end, setEnd] = useState(PRESETS[3].range()[1]);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [drill, setDrill] = useState(null); // {unit_id?, title} — bookings behind a number
  const [peekEmail, setPeekEmail] = useState(null); // customer story drawer
  const [report, setReport] = useState(null); // revenue report data for the PDF
  const [reportLoading, setReportLoading] = useState(false);

  // Build the printable revenue report for the selected range.
  const openReport = async () => {
    setReportLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/analytics/report`, { params: { start_date: start, end_date: end } });
      setReport(data);
    } catch {
      toast.error("Couldn't build the revenue report.");
    } finally {
      setReportLoading(false);
    }
  };
  const printReport = () => setTimeout(() => window.print(), 60);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await axios.get(`${API}/admin/analytics`, { params: { start_date: start, end_date: end } });
      setData(data);
    } catch {
      toast.error("Failed to load analytics.");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, [start, end]);

  const applyPreset = (p) => { const [s, e] = p.range(); setStart(s); setEnd(e); };

  const chartData = useMemo(() => (data?.daily_revenue || []).map(d => ({ ...d, dateShort: d.date.slice(5) })), [data]);

  return (
    <section className="admin-card" data-testid="admin-analytics-card">
      <div className="flex flex-wrap items-end justify-between gap-4 mb-6">
        <div>
          <p className="overline text-[#B76E79]"><span className="marker-line marker-line--rose" /> CRM · numbers</p>
          <h3 className="font-serif text-2xl md:text-3xl mt-1">Analytics</h3>
        </div>
        <div className="flex items-end gap-2 flex-wrap">
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">From</Label>
            <Input type="date" value={start} onChange={(e) => setStart(e.target.value)} className="mt-1 h-10 rounded-lg w-40" data-testid="analytics-start" />
          </div>
          <div>
            <Label className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58]">To</Label>
            <Input type="date" value={end} onChange={(e) => setEnd(e.target.value)} className="mt-1 h-10 rounded-lg w-40" data-testid="analytics-end" />
          </div>
          <button
            onClick={openReport}
            disabled={reportLoading}
            className="h-10 px-4 rounded-lg bg-[#0F0E0E] text-[#F5EFE6] text-[11px] uppercase tracking-[0.16em] inline-flex items-center gap-2 hover:bg-[#8C4E58] transition-colors disabled:opacity-60"
            data-testid="analytics-report-open"
            title="Build a branded revenue report for this date range and save it as a PDF"
          >
            {reportLoading ? <Loader2 size={14} className="animate-spin" /> : <Download size={14} />}
            Revenue PDF
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-2 mb-6">
        {PRESETS.map((p) => (
          <button key={p.key} onClick={() => applyPreset(p)} className="text-[11px] uppercase tracking-[0.14em] px-3 h-8 rounded-full border border-[#DDD1BE] hover:border-[#B76E79] hover:text-[#B76E79]" data-testid={`analytics-preset-${p.key}`}>
            {p.label}
          </button>
        ))}
      </div>

      {loading && <div className="text-center py-8 text-[#6A5F58]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>}

      {!loading && data && (
        <>
          {/* KPI cards — Revenue & Bookings click through to the bookings behind them */}
          <p className="text-[11px] text-[#6A5F58] mb-2">Tap Revenue, Bookings or any lodge to see exactly who stayed, when they booked and how they paid.</p>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
            <KPI icon={<PoundSterling size={16} />} label="Revenue (paid)" value={gbp(data.kpis.revenue_paid)} sub={data.kpis.revenue_pending > 0 ? `+${gbp(data.kpis.revenue_pending)} pending` : "all settled"} tone="green" testid="kpi-revenue" onClick={() => setDrill({ unit_id: null, title: "All bookings" })} />
            <KPI icon={<TrendingUp size={16} />} label="Bookings" value={data.kpis.total_bookings} sub={`${data.kpis.paid_bookings} paid`} testid="kpi-bookings" onClick={() => setDrill({ unit_id: null, title: "All bookings" })} />
            <KPI icon={<PoundSterling size={16} />} label="Avg booking" value={gbp(data.kpis.avg_booking_value)} sub="paid only" testid="kpi-avg" />
            <KPI icon={<Percent size={16} />} label="Best lodge occ." value={`${Math.max(0, ...(data.occupancy.lodges.map(l => l.occupancy_pct)))}%`} sub="in range" testid="kpi-occ" />
          </div>

          {/* Revenue chart */}
          <div className="bg-[#F5EFE6] rounded-2xl p-5 mb-6">
            <p className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58] mb-3">Daily revenue</p>
            <div style={{ width: "100%", height: 220 }}>
              <ResponsiveContainer>
                <LineChart data={chartData} margin={{ top: 6, right: 12, bottom: 0, left: -10 }}>
                  <XAxis dataKey="dateShort" tick={{ fontSize: 10, fill: "#6A5F58" }} interval={Math.max(0, Math.floor(chartData.length / 12))} />
                  <YAxis tick={{ fontSize: 10, fill: "#6A5F58" }} />
                  <Tooltip formatter={(v) => gbp(v)} labelStyle={{ color: "#14100F" }} contentStyle={{ background: "#fff", border: "1px solid #DDD1BE" }} />
                  <Line type="monotone" dataKey="revenue" stroke="#B76E79" strokeWidth={2.4} dot={false} />
                </LineChart>
              </ResponsiveContainer>
            </div>
          </div>

          {/* Occupancy — lodge rows click through to who stayed */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <OccupancyCard title="Lodge occupancy" icon={<Home size={13} />} rows={data.occupancy.lodges.map(l => ({ label: l.name, value: l.occupancy_pct, sub: `${l.booked_nights}/${l.total_nights} nights`, unitId: l.unit_id }))} onRowClick={(r) => setDrill({ unit_id: r.unitId, title: r.label })} />
            <OccupancyCard title="Camping occupancy" icon={<Trees size={13} />} rows={Object.values(data.occupancy.camping).map(c => ({ label: c.label, value: c.occupancy_pct, sub: `${c.booked_nights}/${c.total_nights} pitch-nights` }))} />
          </div>

          {/* Top guests — click a guest for their full story */}
          {data.top_guests.length > 0 && (
            <div className="rounded-2xl border border-[#DDD1BE] overflow-hidden" data-testid="top-guests-table">
              <p className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58] px-5 py-3 bg-[#F5EFE6] border-b border-[#DDD1BE]">Top guests (by spend) — click to open their story</p>
              <table className="w-full text-sm">
                <tbody>
                  {data.top_guests.map((g, i) => (
                    <tr
                      key={g.email}
                      className="border-t border-[#DDD1BE] first:border-t-0 hover:bg-[#FBF7F1] cursor-pointer"
                      onClick={() => setPeekEmail(g.email)}
                      data-testid={`analytics-guest-${i}`}
                    >
                      <td className="px-5 py-3 text-[#6A5F58] w-8 text-right">{i + 1}.</td>
                      <td className="px-3 py-3 font-medium">{g.name || g.email}</td>
                      <td className="px-3 py-3 text-[#6A5F58] text-xs">{g.email}</td>
                      <td className="px-3 py-3 text-right">{g.bookings} bkg{g.bookings > 1 ? "s" : ""}</td>
                      <td className="px-5 py-3 text-right font-medium">{gbp(g.spent)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      )}

      {drill && (
        <BookingsDrilldown
          start={start}
          end={end}
          unitId={drill.unit_id}
          title={drill.title}
          onClose={() => setDrill(null)}
          onPeek={(email) => setPeekEmail(email)}
        />
      )}
      {peekEmail && <CustomerPeek email={peekEmail} onClose={() => setPeekEmail(null)} />}

      {/* Revenue report — preview modal (screen) + print-only sheet (PDF) */}
      {report && (
        <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 no-print" onClick={() => setReport(null)}>
          <div className="w-full max-w-3xl max-h-[88vh] bg-white rounded-2xl overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="revenue-report-modal">
            <div className="p-4 border-b border-[#DDD1BE] flex items-center justify-between gap-3 bg-[#F5EFE6]">
              <div>
                <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">
                  Revenue report · {report.range.start_date} → {report.range.end_date}
                </p>
                <p className="text-[11px] text-[#6A5F58] mt-0.5">
                  Check the preview, then Download PDF — pick "Save as PDF" in the print dialog.
                </p>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={printReport}
                  className="h-9 px-4 rounded-lg bg-[#0F0E0E] text-[#F5EFE6] text-[11px] uppercase tracking-[0.14em] inline-flex items-center gap-2 hover:bg-[#8C4E58] transition-colors"
                  data-testid="revenue-report-download"
                >
                  <Printer size={14} /> Download PDF
                </button>
                <button onClick={() => setReport(null)} className="p-2 rounded-full hover:bg-[#DDD1BE]" data-testid="revenue-report-close">
                  <X size={18} />
                </button>
              </div>
            </div>
            <div className="overflow-y-auto p-5 bg-[#EDE3D2]/40">
              <div className="bg-white shadow-md mx-auto max-w-[800px] p-8">
                <RevenueReportBody data={report} />
              </div>
            </div>
          </div>
        </div>
      )}
      {report && (
        <div className="revenue-report-sheet" data-testid="revenue-report-sheet">
          <RevenueReportBody data={report} />
        </div>
      )}
    </section>
  );
};

const gbp2 = (v) => new Intl.NumberFormat("en-GB", { style: "currency", currency: "GBP" }).format(v || 0);

/** Shared markup for the revenue report — identical on screen and in the PDF. */
const RevenueReportBody = ({ data }) => {
  const { kpis, channels, accommodation, bookings, range } = data;
  return (
    <div className="revenue-report-body">
      <h1>Walnut Lakes</h1>
      <p className="sheet-sub">Holiday Park · Saxilby, Lincolnshire</p>
      <h2>Revenue report — {range.start_date} to {range.end_date}</h2>
      <p className="sheet-sub">
        {range.total_days} day{range.total_days === 1 ? "" : "s"} · generated {new Date(data.generated_at).toLocaleString("en-GB", { day: "numeric", month: "short", year: "numeric", hour: "numeric", minute: "2-digit" })}
      </p>

      <div className="kpi-grid" data-testid="report-kpis">
        <div className="kpi-box"><p className="kpi-label">Revenue (paid)</p><p className="kpi-value">{gbp2(kpis.revenue_paid)}</p></div>
        <div className="kpi-box"><p className="kpi-label">Collected so far</p><p className="kpi-value">{gbp2(kpis.collected)}</p></div>
        <div className="kpi-box"><p className="kpi-label">Still to collect</p><p className="kpi-value">{gbp2(kpis.outstanding)}</p></div>
        <div className="kpi-box"><p className="kpi-label">Bookings</p><p className="kpi-value">{kpis.total_bookings}</p></div>
        <div className="kpi-box"><p className="kpi-label">Nights sold</p><p className="kpi-value">{kpis.nights_sold}</p></div>
        <div className="kpi-box"><p className="kpi-label">Awaiting payment</p><p className="kpi-value">{gbp2(kpis.revenue_pending)}</p></div>
      </div>

      <h2>Revenue by channel</h2>
      <table data-testid="report-channels">
        <thead>
          <tr><th>Channel</th><th className="num">Bookings</th><th className="num">Nights</th><th className="num">Revenue (paid)</th></tr>
        </thead>
        <tbody>
          {channels.map((c) => (
            <tr key={c.label}>
              <td>{c.label}</td>
              <td className="num">{c.bookings}</td>
              <td className="num">{c.nights}</td>
              <td className="num">{gbp2(c.revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Revenue by accommodation</h2>
      <table>
        <thead>
          <tr><th>Type</th><th className="num">Bookings</th><th className="num">Nights</th><th className="num">Revenue (paid)</th></tr>
        </thead>
        <tbody>
          {accommodation.map((a) => (
            <tr key={a.label}>
              <td>{a.label}</td>
              <td className="num">{a.bookings}</td>
              <td className="num">{a.nights}</td>
              <td className="num">{gbp2(a.revenue)}</td>
            </tr>
          ))}
        </tbody>
      </table>

      <h2>Bookings ledger ({bookings.length})</h2>
      <table data-testid="report-ledger">
        <thead>
          <tr>
            <th>Guest</th><th>Unit</th><th>Dates</th><th className="num">Nights</th>
            <th>Channel</th><th>Status</th><th className="num">Total</th><th className="num">Collected</th><th className="num">Due</th>
          </tr>
        </thead>
        <tbody>
          {bookings.map((b, i) => (
            <tr key={i}>
              <td>{b.guest_name}{b.booking_id ? ` · ${b.booking_id}` : ""}</td>
              <td>{b.unit_label}</td>
              <td>{b.start_date} → {b.end_date}</td>
              <td className="num">{b.nights_in_range}</td>
              <td>{b.channel}</td>
              <td>{b.payment_status}</td>
              <td className="num">{gbp2(b.total)}</td>
              <td className="num">{gbp2(b.collected)}</td>
              <td className="num">{b.outstanding > 0 ? gbp2(b.outstanding) : "—"}</td>
            </tr>
          ))}
          <tr>
            <td colSpan={6}><strong>Totals</strong></td>
            <td className="num"><strong>{gbp2(kpis.revenue_paid + kpis.revenue_pending)}</strong></td>
            <td className="num"><strong>{gbp2(kpis.collected)}</strong></td>
            <td className="num"><strong>{gbp2(kpis.outstanding)}</strong></td>
          </tr>
        </tbody>
      </table>

      <p className="sheet-footer">
        Generated from the Walnut Lakes admin console · figures include paid and awaiting-payment stays overlapping the range ·
        channel bookings paid via the channel itself are excluded from revenue.
      </p>
    </div>
  );
};

const gbpDateShort = (iso) => {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
  } catch {
    return String(iso).slice(0, 10);
  }
};

/** Modal listing the actual bookings behind an analytics number (or one lodge's occupancy). */
const BookingsDrilldown = ({ start, end, unitId, title, onClose, onPeek }) => {
  const [rows, setRows] = useState(null);
  const [summary, setSummary] = useState(null);

  useEffect(() => {
    axios
      .get(`${API}/admin/analytics/bookings`, {
        params: { start_date: start, end_date: end, ...(unitId ? { unit_id: unitId } : {}) },
      })
      .then(({ data }) => { setRows(data.bookings || []); setSummary(data.summary || null); })
      .catch(() => { toast.error("Could not load the bookings behind this number."); onClose(); });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [start, end, unitId]);

  return (
    <div className="fixed inset-0 z-50 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4" onClick={onClose}>
      <div className="w-full max-w-4xl max-h-[85vh] bg-[#F5EFE6] rounded-2xl overflow-hidden flex flex-col shadow-2xl" onClick={(e) => e.stopPropagation()} data-testid="analytics-drilldown">
        <div className="p-5 border-b border-[#DDD1BE] flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.18em] text-[#B76E79] font-semibold">{title}</p>
            <h4 className="font-serif text-2xl mt-1" data-testid="drill-summary">
              {summary
                ? `${summary.bookings} booking${summary.bookings === 1 ? "" : "s"} · ${summary.nights_in_range} nights · ${gbp(summary.revenue_paid)} paid${summary.revenue_pending > 0 ? ` · +${gbp(summary.revenue_pending)} pending` : ""}`
                : "Loading…"}
            </h4>
            <p className="text-[11px] text-[#6A5F58] mt-1">{start} → {end} · paid &amp; pending stays overlapping this range</p>
          </div>
          <button onClick={onClose} className="p-2 rounded-full hover:bg-[#DDD1BE]" data-testid="drill-close"><X size={18} /></button>
        </div>
        <div className="overflow-y-auto p-5">
          {!rows && <div className="text-center py-8 text-[#6A5F58]"><Loader2 className="animate-spin inline mr-2" size={16} /> Loading…</div>}
          {rows && rows.length === 0 && <p className="text-center py-8 text-[#6A5F58]" data-testid="drill-empty">No paid or pending bookings in this range.</p>}
          {rows && rows.length > 0 && (
            <div className="space-y-2">
              {rows.map((b) => (
                <button
                  key={b.id}
                  type="button"
                  onClick={() => b.guest_email && onPeek(b.guest_email)}
                  className="w-full text-left bg-white border border-[#DDD1BE] rounded-lg p-3 hover:border-[#B76E79] transition-colors"
                  data-testid={`drill-row-${b.id}`}
                  title="Open customer story"
                >
                  <div className="flex items-start justify-between gap-3 flex-wrap">
                    <div>
                      <p className="font-medium text-[#14100F]">{b.guest_name || b.guest_email} <span className="text-[#6A5F58] font-normal">· {b.unit_label}</span></p>
                      <p className="text-[11px] text-[#6A5F58] mt-0.5">
                        {b.start_date} → {b.end_date} · {b.nights_in_range} of {b.nights} night{b.nights > 1 ? "s" : ""} in range · {b.guests} guest{b.guests > 1 ? "s" : ""}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="font-semibold text-[#14100F]">{gbp(b.total)}</p>
                      <span className={`px-2 py-0.5 rounded-full text-[10px] uppercase tracking-[0.14em] font-semibold ${b.payment_status === "paid" ? "bg-[#3B8C4E] text-white" : "bg-[#C89B5F] text-white"}`}>
                        {b.payment_status}
                      </span>
                    </div>
                  </div>
                  <div className="mt-2 flex flex-wrap gap-1.5">
                    <span className="px-2 py-0.5 rounded-full bg-[#F5EFE6] border border-[#DDD1BE] text-[10px] text-[#3A322E]">via {sourceLabel(b)}</span>
                    <span className="px-2 py-0.5 rounded-full bg-[#F5EFE6] border border-[#DDD1BE] text-[10px] text-[#3A322E]">{paymentLabel(b)}</span>
                    <span className="px-2 py-0.5 rounded-full bg-[#F5EFE6] border border-[#DDD1BE] text-[10px] text-[#3A322E]">Booked {gbpDateShort(b.created_at)}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const KPI = ({ icon, label, value, sub, tone, testid, onClick }) => (
  <div
    className={`bg-white border border-[#DDD1BE] rounded-xl p-4 ${onClick ? "cursor-pointer hover:border-[#B76E79] transition-colors" : ""}`}
    data-testid={testid}
    onClick={onClick}
    role={onClick ? "button" : undefined}
  >
    <p className="text-[10px] uppercase tracking-[0.18em] text-[#6A5F58] flex items-center gap-1.5">{icon} {label}</p>
    <p className={`font-serif text-2xl md:text-3xl mt-2 ${tone === "green" ? "text-[#3B8C4E]" : "text-[#14100F]"}`}>{value}</p>
    <p className="text-[11px] text-[#6A5F58] mt-1">{sub}</p>
  </div>
);

const OccupancyCard = ({ title, icon, rows, onRowClick }) => (
  <div className="bg-white border border-[#DDD1BE] rounded-xl p-5">
    <p className="text-[11px] uppercase tracking-[0.18em] text-[#6A5F58] flex items-center gap-1.5 mb-3">{icon} {title}</p>
    <div className="space-y-3">
      {rows.map((r) => {
        const clickable = !!(onRowClick && r.unitId);
        return (
          <div
            key={r.label}
            className={clickable ? "cursor-pointer rounded-lg -mx-2 px-2 py-1 hover:bg-[#FBF7F1] transition-colors" : ""}
            onClick={clickable ? () => onRowClick(r) : undefined}
            role={clickable ? "button" : undefined}
            data-testid={clickable ? `occ-row-${r.unitId}` : undefined}
            title={clickable ? "See who stayed" : undefined}
          >
            <div className="flex justify-between text-sm mb-1">
              <span className="text-[#14100F]">{r.label}</span>
              <span className="text-[#B76E79] font-semibold">{r.value}%</span>
            </div>
            <div className="h-2 bg-[#F5EFE6] rounded-full overflow-hidden">
              <div className="h-full bg-gradient-to-r from-[#B76E79] to-[#8C4E58] rounded-full transition-all" style={{ width: `${Math.min(100, r.value)}%` }} />
            </div>
            <p className="text-[10px] text-[#6A5F58] mt-1">{r.sub}</p>
          </div>
        );
      })}
    </div>
  </div>
);

export default AdminAnalytics;
